import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { readAllPages } from './readAllPages';
import { fetchUserDirectory } from './userDirectory';
import { MessageSquare, Plus, Users, User, Pin, Send, X, ArrowLeft, Bell, UserPlus, UserMinus, Search, Trash2 } from 'lucide-react';
import { useModalTracking, trackAction } from './usage';
import { formatUserError } from './errorMessage';

// ConversationDetail (messages) and ChatRoomDetail (group chats) send and
// remove members exactly the same way. These two helpers are the one
// implementation both use, so a fix here can't be half-applied.

// Returns null on success, or a ready-to-show message on failure. A send that
// fails used to leave the typed text sitting in the box with no explanation.
async function sendMessageRow({ conversationId, senderId, content, parentMessageId }) {
  const { error } = await supabase.from('messages').insert({
    conversation_id: conversationId,
    sender_id: senderId,
    content,
    parent_message_id: parentMessageId || null,
  });
  if (error) return 'Message not sent: ' + formatUserError(error);
  return null;
}

// Returns null on success, or a ready-to-show message on failure.
//
// QA: an empty result is NOT proof of failure. The database applies the read
// rules to the deleted row before handing it back, so a removal the user IS
// allowed to make can come back with nothing to show — and the old code called
// that a failure, telling coaches a member had not been removed when he had.
// Only a real error is a failure on its own. When the result is merely empty,
// ask the server who is still in the conversation and let that answer decide.
async function removeParticipantRow(conversationId, memberId) {
  const { data, error } = await supabase
    .from('conversation_participants')
    .delete()
    .eq('conversation_id', conversationId)
    .eq('user_id', memberId)
    .select('user_id');
  if (error) return 'Could not remove that member: ' + formatUserError(error);
  if (data && data.length > 0) return null;
  const { data: stillThere, error: checkError } = await supabase
    .from('conversation_participants')
    .select('user_id')
    .eq('conversation_id', conversationId)
    .eq('user_id', memberId);
  // Still listed = genuinely not removed. Anything else (gone, or the check
  // itself failed) is not a failure we have established, so claim nothing and
  // let the refreshed list on screen show what really happened.
  if (!checkError && stillThere && stillThere.length > 0) {
    return 'That member is still in this conversation. You may not have permission to remove people here — ask an admin or a coach to do it.';
  }
  return null;
}

export default function Messages({ userId, userRole, initialConversationId, onInitialConversationHandled }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);

  const messageConvos = conversations.filter(c => c.type === 'direct' || c.type === 'team_announcement');
  const chatRooms = conversations.filter(c => c.type === 'group');

  // Refs mirror the current selection so the realtime callback (bound once with
  // [userId] deps) reads the live value instead of the mount-time null.
  const selectedConvRef = useRef(null);
  const selectedChatRef = useRef(null);
  useEffect(() => { selectedConvRef.current = selectedConversation; }, [selectedConversation]);
  useEffect(() => { selectedChatRef.current = selectedChat; }, [selectedChat]);

  useEffect(() => {
    fetchConversations();
    fetchTeams();
    fetchUsers();

    // Subscribe to real-time message updates. Debounce the refetch so a burst
    // of inserts (e.g. someone bulk-sending) coalesces into one round trip
    // instead of N. Use a per-userId channel name so two simultaneous mounts
    // (portal switch) don't collide on the same subscription.
    let refetchTimer = null;
    const debouncedRefetch = () => {
      if (refetchTimer) return;
      refetchTimer = setTimeout(() => {
        refetchTimer = null;
        fetchConversations();
        if (selectedConvRef.current) {
          fetchConversationDetail(selectedConvRef.current.id, 'message');
        }
        if (selectedChatRef.current) {
          fetchConversationDetail(selectedChatRef.current.id, 'chat');
        }
      }, 200);
    };
    const subscription = supabase
      .channel(`messages-${userId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'messages' },
        debouncedRefetch
      )
      .subscribe();

    return () => {
      if (refetchTimer) clearTimeout(refetchTimer);
      subscription.unsubscribe();
    };
  }, [userId]);

  // #407: arrived here from "Message coach" on a session — open that thread as
  // soon as the list has loaded (the conversation was created a moment ago, so
  // it is in the list). Clear the request so a later visit starts normally.
  useEffect(() => {
    if (!initialConversationId || loading) return;
    fetchConversationDetail(initialConversationId, 'message');
    if (onInitialConversationHandled) onInitialConversationHandled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConversationId, loading]);

  const fetchConversations = async () => {
    // Step 1: Get my conversation IDs
    const { data: participantData, error: partError } = await supabase
      .from('conversation_participants')
      .select('conversation_id')
      .eq('user_id', userId);

    if (partError || !participantData || participantData.length === 0) {
      setConversations([]);
      setLoading(false);
      return;
    }

    const conversationIds = participantData.map(p => p.conversation_id);

    // Step 2: Get conversation details
    const { data: convData, error: convError } = await supabase
      .from('conversations')
      .select('*')
      .in('id', conversationIds)
      .order('updated_at', { ascending: false });

    if (convError || !convData) {
      setConversations([]);
      setLoading(false);
      return;
    }

    // Step 2b: Participants (for DM peer names) and unread counts, fetched in
    // bulk rather than hard-coded to empty/0.
    const [{ data: parts }, { data: convMsgs }, { data: myReads }] = await Promise.all([
      supabase
        .from('conversation_participants')
        .select('conversation_id, user_id')
        .in('conversation_id', conversationIds),
      supabase
        .from('messages')
        .select('id, conversation_id, sender_id')
        .in('conversation_id', conversationIds)
        .is('deleted_at', null),
      supabase
        .from('message_reads')
        .select('message_id')
        .eq('user_id', userId),
    ]);
    // Peer names come from user_directory, not an embed on `users`. Without
    // this every DM in the list is titled "Direct Message" and the threads
    // become impossible to tell apart — with no error to explain why.
    const partNames = await fetchUserDirectory((parts || []).map(p => p.user_id));
    const partsByConv = {};
    (parts || []).forEach(p => {
      const withUser = { ...p, users: partNames.get(p.user_id) || null };
      (partsByConv[p.conversation_id] = partsByConv[p.conversation_id] || []).push(withUser);
    });
    const readSet = new Set((myReads || []).map(r => r.message_id));
    const unreadByConv = {};
    (convMsgs || []).forEach(m => {
      if (m.sender_id !== userId && !readSet.has(m.id)) {
        unreadByConv[m.conversation_id] = (unreadByConv[m.conversation_id] || 0) + 1;
      }
    });

    // Step 3: Enhance with messages and team info
    const enhanced = await Promise.all(
      convData.map(async (conv) => {
        const { data: msgs } = await supabase
          .from('messages')
          .select('id, content, created_at, sender_id')
          .eq('conversation_id', conv.id)
          .order('created_at', { ascending: false })
          .limit(1);

        let teamName = null;
        if (conv.team_id) {
          const { data: team } = await supabase
            .from('teams')
            .select('name')
            .eq('id', conv.team_id)
            .single();
          teamName = team?.name;
        }

        return {
          ...conv,
          messages: msgs || [],
          lastMessage: msgs?.[0],
          teams: teamName ? { name: teamName } : null,
          unreadCount: unreadByConv[conv.id] || 0,
          conversation_participants: partsByConv[conv.id] || []
        };
      })
    );

    setConversations(enhanced);
    setLoading(false);
  };

  const fetchTeams = async () => {
    if (userRole === 'admin' || userRole === 'coach') {
      const { data } = await supabase.from('teams').select('*').order('name');
      if (data) setTeams(data);
    }
  };

  const fetchUsers = async () => {
    if (userRole === 'admin' || userRole === 'coach') {
      // #437: paged — users is past the 1,000-row clamp, which was silently
      // dropping late-alphabet names from the recipient picker.
      const { rows, error } = await readAllPages(() => supabase
        .from('users')
        .select('id, full_name, role, is_intern')
        .order('full_name')
        .order('id'));
      if (error) console.error('Messages: users query failed:', error);
      setUsers(rows);
    }
  };

  const fetchConversationDetail = async (conversationId, target) => {
    const { data: conv, error: convError } = await supabase
      .from('conversations')
      .select('*')
      .eq('id', conversationId)
      .single();

    if (convError || !conv) return;

    const { data: participants } = await supabase
      .from('conversation_participants')
      .select('user_id')
      .eq('conversation_id', conversationId);

    const { data: messages } = await supabase
      .from('messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });

    // One batched directory lookup covering everyone on this screen -- the
    // participants and every message sender together. This was previously a
    // sequential lookup per participant and per message, so opening a long
    // thread fired one request per message: a single conversation was measured
    // putting ~3,900 calls on user_directory in two minutes, and the ones that
    // landed across a token refresh came back 401 (permission denied for anon).
    const directory = await fetchUserDirectory([
      ...(participants || []).map(p => p.user_id),
      ...(messages || []).map(m => m.sender_id)
    ]);

    const participantsWithUsers = (participants || [])
      .filter(p => directory.has(p.user_id))
      .map(p => ({ user_id: p.user_id, users: directory.get(p.user_id) }));

    const messagesWithSenders = (messages || []).map(msg => ({
      ...msg,
      sender: directory.get(msg.sender_id) ||
        { id: msg.sender_id, full_name: 'Unknown', role: 'unknown' }
    }));

    let teamData = null;
    if (conv.team_id) {
      const { data: team } = await supabase
        .from('teams')
        .select('name')
        .eq('id', conv.team_id)
        .single();
      teamData = team;
    }

    const fullConversation = {
      ...conv,
      conversation_participants: participantsWithUsers,
      messages: messagesWithSenders,
      teams: teamData
    };

    if (target === 'chat') {
      setSelectedChat(fullConversation);
    } else {
      setSelectedConversation(fullConversation);
    }

    // Mark messages as read. Read state lives in message_reads (there's no `read`
    // column on messages), so check against existing reads and upsert only the new
    // ones — the previous plain insert re-ran every open and violated the
    // (message_id, user_id) unique constraint with an unhandled rejection.
    const receivedIds = messagesWithSenders
      .filter(m => m.sender_id !== userId)
      .map(m => m.id);
    if (receivedIds.length > 0) {
      const { data: existingReads } = await supabase
        .from('message_reads')
        .select('message_id')
        .eq('user_id', userId)
        .in('message_id', receivedIds);
      const alreadyRead = new Set((existingReads || []).map(r => r.message_id));
      const toInsert = receivedIds.filter(id => !alreadyRead.has(id));
      if (toInsert.length > 0) {
        await supabase.from('message_reads').upsert(
          toInsert.map(id => ({ message_id: id, user_id: userId })),
          { onConflict: 'message_id,user_id', ignoreDuplicates: true }
        );
        fetchConversations();
      }
    }
  };

  const togglePin = async (conversationId, currentPinned) => {
    // Only the creator, an admin or a coach may update a conversation. Without
    // this check a pin someone isn't allowed to set flips on screen and quietly
    // reverts on the next refresh.
    const { error } = await supabase
      .from('conversations')
      .update({ is_pinned: !currentPinned })
      .eq('id', conversationId);
    if (error) { alert('Could not change the pin: ' + formatUserError(error)); return; }
    fetchConversations();
  };

  const deleteConversation = async (conversationId) => {
    // Deleting the conversation row is enough — messages and participants are
    // removed with it by the database (ON DELETE CASCADE).
    //
    // Do NOT remove it from the screen first — a delete the user isn't allowed
    // to make would let the thread disappear and come straight back on refresh.
    //
    // QA: but an empty result is not proof of failure either (see
    // removeParticipantRow). When nothing comes back, ask the server whether
    // the conversation is still there and let that decide.
    const { data, error } = await supabase
      .from('conversations')
      .delete()
      .eq('id', conversationId)
      .select('id');
    if (error) { alert('Could not delete this conversation: ' + formatUserError(error)); return; }
    if (!data || data.length === 0) {
      const { data: stillThere, error: checkError } = await supabase
        .from('conversations').select('id').eq('id', conversationId);
      // QA: nothing came back from the delete AND the re-check failed, so
      // whether the conversation is gone is genuinely unknown. Taking it off
      // the screen would claim a success we have not established (it comes
      // back on the next reload); saying it failed would claim the opposite.
      // Show what the server reports right now and say plainly that we don't
      // know.
      if (checkError) {
        alert('We could not tell whether this conversation was deleted. The list below has been refreshed — please check whether it is still there.');
        fetchConversations();
        return;
      }
      if (stillThere && stillThere.length > 0) {
        alert('This conversation is still here. You may not have permission to delete it — ask an admin or a coach to do it.');
        fetchConversations();
        return;
      }
    }

    setConversations(prev => prev.filter(c => c.id !== conversationId));
    if (selectedConversation?.id === conversationId) setSelectedConversation(null);
    if (selectedChat?.id === conversationId) setSelectedChat(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-600">Loading communication...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900">Communication</h2>
        <p className="text-gray-600 mt-1">Direct messages, announcements, and group chats</p>
      </div>

      {/* ========== MESSAGES SECTION ========== */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Section Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-blue-50 border-b border-blue-100">
          <div className="flex items-center space-x-2">
            <MessageSquare size={18} className="text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Messages</h3>
            <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded-full border border-gray-200">{messageConvos.length}</span>
          </div>
          {(userRole === 'admin' || userRole === 'coach') && (
            <button
              onClick={() => setShowNewMessage(true)}
              className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition flex items-center space-x-1.5"
            >
              <Plus size={16} />
              <span>New Message</span>
            </button>
          )}
        </div>

        {/* List + Detail in one row */}
        <div className="flex flex-col sm:flex-row h-[70vh] sm:h-[450px]">
          {/* Conversation List — hidden on mobile when a conversation is open so
              the detail takes full height and the user does not have to scroll
              past the list to see messages. sm+ keeps the side-by-side layout. */}
          <div className={`w-full sm:w-72 flex-shrink-0 max-h-52 sm:max-h-none border-b sm:border-b-0 sm:border-r border-gray-200 flex-col ${selectedConversation ? 'hidden sm:flex' : 'flex'}`}>
            <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
              {messageConvos.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  <MessageSquare size={32} className="mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No messages yet</p>
                </div>
              ) : (
                messageConvos.map((conv) => (
                  <ConversationItem
                    key={conv.id}
                    conversation={conv}
                    isSelected={selectedConversation?.id === conv.id}
                    onClick={() => fetchConversationDetail(conv.id, 'message')}
                    onTogglePin={() => togglePin(conv.id, conv.is_pinned)}
                    onDelete={() => deleteConversation(conv.id)}
                    userId={userId}
                    userRole={userRole}
                  />
                ))
              )}
            </div>
          </div>

          {/* Detail */}
          <div className="flex-1 min-w-0">
            {selectedConversation ? (
              <ConversationDetail
                conversation={selectedConversation}
                userId={userId}
                userRole={userRole}
                users={users}
                onBack={() => setSelectedConversation(null)}
                onRefresh={() => fetchConversationDetail(selectedConversation.id, 'message')}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <MessageSquare size={48} className="mb-3 text-gray-300" />
                <p className="font-medium text-gray-500">Select a conversation</p>
                <p className="text-sm">Choose from the list to view messages</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ========== CHATS SECTION ========== */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Section Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-green-50 border-b border-green-100">
          <div className="flex items-center space-x-2">
            <Users size={18} className="text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">Chats</h3>
            <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded-full border border-gray-200">{chatRooms.length}</span>
          </div>
          {(userRole === 'admin' || userRole === 'coach') && (
            <button
              onClick={() => setShowNewChat(true)}
              className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-green-700 transition flex items-center space-x-1.5"
            >
              <Plus size={16} />
              <span>New Chat</span>
            </button>
          )}
        </div>

        {/* List + Detail in one row */}
        <div className="flex flex-col sm:flex-row h-[70vh] sm:h-[450px]">
          {/* Chat List — same mobile-hide pattern as the Messages section above. */}
          <div className={`w-full sm:w-72 flex-shrink-0 max-h-52 sm:max-h-none border-b sm:border-b-0 sm:border-r border-gray-200 flex-col ${selectedChat ? 'hidden sm:flex' : 'flex'}`}>
            <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
              {chatRooms.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  <Users size={32} className="mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No chats yet</p>
                </div>
              ) : (
                chatRooms.map((conv) => (
                  <ConversationItem
                    key={conv.id}
                    conversation={conv}
                    isSelected={selectedChat?.id === conv.id}
                    onClick={() => fetchConversationDetail(conv.id, 'chat')}
                    onTogglePin={() => togglePin(conv.id, conv.is_pinned)}
                    onDelete={() => deleteConversation(conv.id)}
                    userId={userId}
                    userRole={userRole}
                  />
                ))
              )}
            </div>
          </div>

          {/* Detail */}
          <div className="flex-1 min-w-0">
            {selectedChat ? (
              <ChatRoomDetail
                conversation={selectedChat}
                userId={userId}
                userRole={userRole}
                users={users}
                onBack={() => setSelectedChat(null)}
                onRefresh={() => fetchConversationDetail(selectedChat.id, 'chat')}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <Users size={48} className="mb-3 text-gray-300" />
                <p className="font-medium text-gray-500">Select a chat</p>
                <p className="text-sm">Choose from the list to start chatting</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showNewMessage && (
        <NewMessageModal
          teams={teams}
          users={users}
          userId={userId}
          userRole={userRole}
          onClose={() => setShowNewMessage(false)}
          onSuccess={() => {
            setShowNewMessage(false);
            fetchConversations();
          }}
        />
      )}

      {showNewChat && (
        <NewChatRoomModal
          users={users}
          userId={userId}
          onClose={() => setShowNewChat(false)}
          onSuccess={() => {
            setShowNewChat(false);
            fetchConversations();
          }}
        />
      )}
    </div>
  );
}

function ConversationItem({ conversation, isSelected, onClick, onTogglePin, onDelete, userId, userRole }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isCreator = conversation.created_by === userId;

  const getConversationTitle = () => {
    if (conversation.type === 'team_announcement') {
      return `${conversation.teams?.name || 'Team'} - ${conversation.title}`;
    } else if (conversation.type === 'group') {
      return conversation.title;
    } else {
      const otherParticipant = conversation.conversation_participants?.find(
        p => p.user_id !== userId
      );
      return otherParticipant?.users?.full_name || 'Direct Message';
    }
  };

  const getIcon = () => {
    if (conversation.type === 'team_announcement' || conversation.type === 'group') {
      return <Users size={16} />;
    }
    return <User size={16} />;
  };

  if (confirmDelete) {
    return (
      <div className="p-4 bg-red-50 border-l-4 border-red-500">
        <p className="text-sm font-medium text-red-800 mb-2">Delete this {conversation.type === 'group' ? 'chat' : 'conversation'}?</p>
        <p className="text-xs text-red-600 mb-3">This will permanently delete all messages.</p>
        <div className="flex space-x-2">
          <button
            onClick={(e) => { e.stopPropagation(); setConfirmDelete(false); }}
            className="flex-1 text-xs py-1.5 px-3 bg-white border border-gray-300 text-gray-700 rounded font-medium hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="flex-1 text-xs py-1.5 px-3 bg-red-600 text-white rounded font-medium hover:bg-red-700 transition"
          >
            Delete
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className={`p-4 cursor-pointer hover:bg-gray-50 transition ${
        isSelected ? 'bg-blue-50 border-l-4 border-blue-600' : ''
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            {getIcon()}
            <h4 className="font-semibold text-gray-900 truncate text-sm">
              {getConversationTitle()}
            </h4>
            {conversation.is_pinned && (
              <Pin size={14} className="text-orange-500 flex-shrink-0" />
            )}
          </div>
          {conversation.lastMessage && (
            <p className="text-xs text-gray-600 truncate">
              {conversation.lastMessage.content}
            </p>
          )}
          <p className="text-xs text-gray-400 mt-1">
            {conversation.updated_at &&
              new Date(conversation.updated_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex flex-col items-end space-y-1 ml-2">
          {conversation.unreadCount > 0 && (
            <span className="bg-blue-600 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1.5">
              {conversation.unreadCount}
            </span>
          )}
          {(userRole === 'admin' || userRole === 'coach') && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onTogglePin();
              }}
              className="text-gray-400 hover:text-orange-500 transition"
            >
              <Pin size={14} />
            </button>
          )}
          {isCreator && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setConfirmDelete(true);
              }}
              className="text-gray-400 hover:text-red-500 transition"
              title="Delete"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function MemberPanel({ conversation, userId, userRole, users, onAddMember, onRemoveMember, onClose }) {
  const [searchQuery, setSearchQuery] = useState('');
  const isCreator = conversation.created_by === userId;
  const canManage = isCreator || userRole === 'admin' || userRole === 'coach';

  const participantIds = (conversation.conversation_participants || []).map(p => p.user_id);
  const availableUsers = users.filter(u =>
    !participantIds.includes(u.id) &&
    u.full_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="w-72 border-l border-gray-200 flex flex-col h-full bg-gray-50">
      <div className="p-3 border-b border-gray-200 flex items-center justify-between bg-white">
        <h4 className="font-semibold text-gray-900 text-sm">Members ({participantIds.length})</h4>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-1">
        {(conversation.conversation_participants || []).map(p => (
          <div key={p.user_id} className="flex items-center justify-between p-2 rounded hover:bg-gray-100">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {p.users?.full_name || 'Unknown'}
              </p>
              <div className="flex items-center space-x-1">
                <span className="text-xs text-gray-500">{p.users?.role}</span>
                {p.user_id === conversation.created_by && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">Creator</span>
                )}
              </div>
            </div>
            {canManage && p.user_id !== userId && (
              <button
                onClick={() => onRemoveMember(p.user_id)}
                className="text-gray-400 hover:text-red-500 ml-2"
                title="Remove member"
              >
                <UserMinus size={14} />
              </button>
            )}
          </div>
        ))}
      </div>

      {canManage && (
        <div className="border-t border-gray-200 p-3 bg-white">
          <p className="text-xs font-medium text-gray-700 mb-2">Add Members</p>
          <div className="relative mb-2">
            <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search users..."
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {availableUsers.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-2">
                {searchQuery ? 'No users found' : 'All users added'}
              </p>
            ) : (
              availableUsers.map(user => (
                <button
                  key={user.id}
                  onClick={() => onAddMember(user.id)}
                  className="w-full flex items-center justify-between p-2 text-left rounded hover:bg-gray-50 text-sm"
                >
                  <span className="truncate">{user.full_name}</span>
                  <UserPlus size={14} className="text-blue-600 flex-shrink-0 ml-2" />
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ConversationDetail({ conversation, userId, userRole, users, onBack, onRefresh }) {
  const [newMessage, setNewMessage] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [sending, setSending] = useState(false);
  const [showMemberPanel, setShowMemberPanel] = useState(false);

  const canReply = !conversation.replies_disabled || userRole === 'admin' || userRole === 'coach';

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    trackAction('send_message');
    setSending(true);
    const failure = await sendMessageRow({
      conversationId: conversation.id,
      senderId: userId,
      content: newMessage.trim(),
      parentMessageId: replyingTo?.id,
    });
    setSending(false);
    if (failure) { alert(failure); return; }
    setNewMessage('');
    setReplyingTo(null);
    onRefresh();
  };

  const handleAddMember = async (memberId) => {
    const { error } = await supabase.from('conversation_participants').insert({
      conversation_id: conversation.id,
      user_id: memberId
    });
    if (!error) onRefresh();
  };

  const handleRemoveMember = async (memberId) => {
    const failure = await removeParticipantRow(conversation.id, memberId);
    if (failure) { alert(failure); return; }
    onRefresh();
  };

  const getReplies = (messageId) => {
    return conversation.messages.filter(m => m.parent_message_id === messageId);
  };

  const topLevelMessages = conversation.messages.filter(m => !m.parent_message_id);
  const isTeamAnnouncement = conversation.type === 'team_announcement';

  return (
    <div className="flex h-full">
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={onBack}
              className="sm:hidden text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h3 className="font-semibold text-gray-900">
                {conversation.type === 'team_announcement'
                  ? `${conversation.teams?.name} - ${conversation.title}`
                  : 'Direct Message'
                }
              </h3>
              {isTeamAnnouncement ? (
                <button
                  onClick={() => setShowMemberPanel(!showMemberPanel)}
                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {conversation.conversation_participants?.length || 0} members
                </button>
              ) : (
                <p className="text-xs text-gray-500">
                  {conversation.conversation_participants?.length || 0} participants
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {isTeamAnnouncement && (
              <button
                onClick={() => setShowMemberPanel(!showMemberPanel)}
                className={`p-1.5 rounded transition ${showMemberPanel ? 'bg-blue-100 text-blue-700' : 'text-gray-400 hover:text-gray-600'}`}
                title="Manage members"
              >
                <Users size={18} />
              </button>
            )}
            {conversation.type === 'team_announcement' && (
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium flex items-center space-x-1">
                <Bell size={12} />
                <span>Announcement</span>
              </span>
            )}
            {conversation.replies_disabled && (
              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                Replies Off
              </span>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {topLevelMessages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              isOwn={message.sender_id === userId}
              onReply={canReply ? () => setReplyingTo(message) : null}
              replies={getReplies(message.id)}
              userId={userId}
            />
          ))}
        </div>

        {/* Reply indicator */}
        {replyingTo && canReply && (
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm">
              <span className="text-gray-600">Replying to </span>
              <span className="font-medium">{replyingTo.sender.full_name}</span>
            </div>
            <button
              onClick={() => setReplyingTo(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Input */}
        {canReply ? (
          <form onSubmit={handleSend} className="p-4 border-t border-gray-200">
            <div className="flex space-x-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={sending || !newMessage.trim()}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center space-x-2"
              >
                <Send size={18} />
              </button>
            </div>
          </form>
        ) : (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-600 text-center italic flex items-center justify-center space-x-2">
              <span>Replies are disabled for this announcement</span>
            </p>
          </div>
        )}
      </div>

      {/* Member Panel (for team announcements) */}
      {showMemberPanel && isTeamAnnouncement && (
        <MemberPanel
          conversation={conversation}
          userId={userId}
          userRole={userRole}
          users={users}
          onAddMember={handleAddMember}
          onRemoveMember={handleRemoveMember}
          onClose={() => setShowMemberPanel(false)}
        />
      )}
    </div>
  );
}

function ChatRoomDetail({ conversation, userId, userRole, users, onBack, onRefresh }) {
  const [newMessage, setNewMessage] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [sending, setSending] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const canReply = !conversation.replies_disabled || userRole === 'admin' || userRole === 'coach';
  const isCreator = conversation.created_by === userId;
  const canManage = isCreator || userRole === 'admin' || userRole === 'coach';

  const participantIds = (conversation.conversation_participants || []).map(p => p.user_id);
  const availableUsers = users.filter(u =>
    !participantIds.includes(u.id) &&
    u.full_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    trackAction('send_message');
    setSending(true);
    const failure = await sendMessageRow({
      conversationId: conversation.id,
      senderId: userId,
      content: newMessage.trim(),
      parentMessageId: replyingTo?.id,
    });
    setSending(false);
    if (failure) { alert(failure); return; }
    setNewMessage('');
    setReplyingTo(null);
    onRefresh();
  };

  const handleAddMember = async (memberId) => {
    const { error } = await supabase.from('conversation_participants').insert({
      conversation_id: conversation.id,
      user_id: memberId
    });
    if (!error) onRefresh();
  };

  const handleRemoveMember = async (memberId) => {
    const failure = await removeParticipantRow(conversation.id, memberId);
    if (failure) { alert(failure); return; }
    onRefresh();
  };

  const getReplies = (messageId) => {
    return conversation.messages.filter(m => m.parent_message_id === messageId);
  };

  const topLevelMessages = conversation.messages.filter(m => !m.parent_message_id);

  return (
    <div className="flex h-full">
      {/* Chat Messages Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={onBack}
              className="sm:hidden text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h3 className="font-semibold text-gray-900">{conversation.title}</h3>
              <p className="text-xs text-gray-500">
                {conversation.conversation_participants?.length || 0} members
              </p>
            </div>
          </div>
          {conversation.replies_disabled && (
            <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
              Replies Off
            </span>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {topLevelMessages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              isOwn={message.sender_id === userId}
              onReply={canReply ? () => setReplyingTo(message) : null}
              replies={getReplies(message.id)}
              userId={userId}
            />
          ))}
        </div>

        {/* Reply indicator */}
        {replyingTo && canReply && (
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm">
              <span className="text-gray-600">Replying to </span>
              <span className="font-medium">{replyingTo.sender.full_name}</span>
            </div>
            <button
              onClick={() => setReplyingTo(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Input */}
        {canReply ? (
          <form onSubmit={handleSend} className="p-4 border-t border-gray-200">
            <div className="flex space-x-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={sending || !newMessage.trim()}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center space-x-2"
              >
                <Send size={18} />
              </button>
            </div>
          </form>
        ) : (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-600 text-center italic">
              Replies are disabled for this chat
            </p>
          </div>
        )}
      </div>

      {/* Always-visible Member Sidebar */}
      <div className="w-56 border-l border-gray-200 flex flex-col h-full bg-gray-50 hidden lg:flex">
        <div className="p-3 border-b border-gray-200 bg-white">
          <h4 className="font-semibold text-gray-900 text-sm">Members ({participantIds.length})</h4>
        </div>

        {/* Member list */}
        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {(conversation.conversation_participants || []).map(p => (
            <div key={p.user_id} className="flex items-center justify-between p-2 rounded hover:bg-gray-100">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {p.users?.full_name || 'Unknown'}
                </p>
                <div className="flex items-center space-x-1">
                  <span className="text-xs text-gray-500">{p.users?.role}</span>
                  {p.user_id === conversation.created_by && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">Creator</span>
                  )}
                </div>
              </div>
              {canManage && p.user_id !== userId && (
                <button
                  onClick={() => handleRemoveMember(p.user_id)}
                  className="text-gray-400 hover:text-red-500 ml-1"
                  title="Remove member"
                >
                  <UserMinus size={14} />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Invite More */}
        {canManage && (
          <div className="border-t border-gray-200 p-3 bg-white">
            {!showInvite ? (
              <button
                onClick={() => setShowInvite(true)}
                className="w-full flex items-center justify-center space-x-2 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition"
              >
                <UserPlus size={16} />
                <span>Invite More</span>
              </button>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-gray-700">Add Members</p>
                  <button onClick={() => { setShowInvite(false); setSearchQuery(''); }} className="text-gray-400 hover:text-gray-600">
                    <X size={14} />
                  </button>
                </div>
                <div className="relative mb-2">
                  <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search users..."
                    className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {availableUsers.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-2">
                      {searchQuery ? 'No users found' : 'All users added'}
                    </p>
                  ) : (
                    availableUsers.map(user => (
                      <button
                        key={user.id}
                        onClick={() => handleAddMember(user.id)}
                        className="w-full flex items-center justify-between p-2 text-left rounded hover:bg-gray-50 text-sm"
                      >
                        <span className="truncate">{user.full_name}</span>
                        <UserPlus size={14} className="text-blue-600 flex-shrink-0 ml-1" />
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MessageBubble({ message, isOwn, onReply, replies, userId }) {
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
        {!isOwn && (
          <div className="flex items-center space-x-2 mb-1">
            <span className="text-xs font-semibold text-gray-900">
              {message.sender?.full_name}
            </span>
            <span className="text-xs text-gray-500">
              {message.sender?.role}
            </span>
          </div>
        )}
        <div
          className={`rounded-lg px-4 py-2 ${
            isOwn
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-900'
          }`}
        >
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        </div>
        <div className="flex items-center space-x-2 mt-1">
          <span className="text-xs text-gray-500">
            {new Date(message.created_at).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </span>
          {!isOwn && onReply && (
            <button
              onClick={onReply}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              Reply
            </button>
          )}
        </div>

        {replies && replies.length > 0 && (
          <div className="mt-2 space-y-2 pl-4 border-l-2 border-gray-300">
            {replies.map((reply) => (
              <MessageBubble
                key={reply.id}
                message={reply}
                isOwn={reply.sender_id === userId}
                onReply={onReply}
                userId={userId}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function NewMessageModal({ teams, users, userId, userRole, onClose, onSuccess }) {
  useModalTracking('NewMessageModal');
  const [messageType, setMessageType] = useState('direct');
  const [repliesDisabled, setRepliesDisabled] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    teamId: '',
    recipientIds: [],
    content: ''
  });
  const [recipientSearch, setRecipientSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (messageType === 'direct' && formData.recipientIds.length === 0) {
      setError('Please select at least one recipient.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const conversationData = {
        type: messageType,
        created_by: userId,
        is_pinned: false,
        replies_disabled: messageType !== 'direct' ? repliesDisabled : false
      };

      if (messageType === 'team_announcement') {
        conversationData.title = formData.title;
        conversationData.team_id = formData.teamId;
      }

      const { data: conversation, error: convError } = await supabase
        .from('conversations')
        .insert(conversationData)
        .select()
        .single();

      if (convError) throw convError;

      const participants = [];

      if (messageType === 'team_announcement') {
        const { data: teamMembers, error: tmError } = await supabase
          .from('team_members')
          .select('user_id')
          .eq('team_id', formData.teamId);

        if (tmError) throw tmError;
        if (!teamMembers || teamMembers.length === 0) {
          throw new Error('No members found for the selected team.');
        }

        participants.push(...teamMembers.map(tm => ({
          conversation_id: conversation.id,
          user_id: tm.user_id
        })));

        if (!teamMembers.some(tm => tm.user_id === userId)) {
          participants.push({
            conversation_id: conversation.id,
            user_id: userId
          });
        }
      } else {
        participants.push({
          conversation_id: conversation.id,
          user_id: userId
        });
        formData.recipientIds.forEach(recipientId => {
          participants.push({
            conversation_id: conversation.id,
            user_id: recipientId
          });
        });
      }

      const { error: participantsError } = await supabase
        .from('conversation_participants')
        .insert(participants);

      if (participantsError) throw participantsError;

      const { error: messageError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversation.id,
          sender_id: userId,
          content: formData.content
        });

      if (messageError) throw messageError;

      setLoading(false);
      onSuccess();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">New Message</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Message Type - only Direct Message and Team Announcement */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message Type *
            </label>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => setMessageType('direct')}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition ${
                  messageType === 'direct'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Direct Message
              </button>
              <button
                type="button"
                onClick={() => setMessageType('team_announcement')}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition ${
                  messageType === 'team_announcement'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Team Announcement
              </button>
            </div>
          </div>

          {/* Title (for announcements) */}
          {messageType === 'team_announcement' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                required
                placeholder="e.g., Practice Update, Game Day Info"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Team selection (for announcements) */}
          {messageType === 'team_announcement' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Team *
              </label>
              <select
                required
                value={formData.teamId}
                onChange={(e) => setFormData({...formData, teamId: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select team</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Recipient selection (for direct messages) */}
          {messageType === 'direct' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipients * <span className="text-xs font-normal text-gray-500">({formData.recipientIds.length} selected)</span>
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {[
                  { label: 'All Coaches', match: (u) => u.role === 'coach' && !u.is_intern },
                  { label: 'All Players', match: (u) => u.role === 'player' },
                  { label: 'All Interns', match: (u) => u.role === 'coach' && u.is_intern },
                  { label: 'All Admins', match: (u) => u.role === 'admin' },
                ].map(group => {
                  const ids = users.filter(u => u.id !== userId && group.match(u)).map(u => u.id);
                  if (ids.length === 0) return null;
                  const allSelected = ids.every(id => formData.recipientIds.includes(id));
                  return (
                    <button
                      type="button"
                      key={group.label}
                      onClick={() => {
                        const set = new Set(formData.recipientIds);
                        if (allSelected) ids.forEach(id => set.delete(id));
                        else ids.forEach(id => set.add(id));
                        setFormData({ ...formData, recipientIds: Array.from(set) });
                      }}
                      className={`text-xs px-2.5 py-1 rounded-full border transition ${
                        allSelected
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {allSelected ? 'Clear' : 'Add'} {group.label}
                    </button>
                  );
                })}
                {formData.recipientIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, recipientIds: [] })}
                    className="text-xs px-2.5 py-1 rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <input
                type="text"
                value={recipientSearch}
                onChange={(e) => setRecipientSearch(e.target.value)}
                placeholder="Search by name or role..."
                className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                {users
                  .filter(u => u.id !== userId)
                  .filter(u => {
                    const q = recipientSearch.trim().toLowerCase();
                    if (!q) return true;
                    const label = u.is_intern ? 'intern' : (u.role || '');
                    return (u.full_name || '').toLowerCase().includes(q) || label.toLowerCase().includes(q);
                  })
                  .map(user => (
                  <label
                    key={user.id}
                    className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={formData.recipientIds.includes(user.id)}
                      onChange={() => {
                        const set = new Set(formData.recipientIds);
                        if (set.has(user.id)) set.delete(user.id);
                        else set.add(user.id);
                        setFormData({ ...formData, recipientIds: Array.from(set) });
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-900">{user.full_name}</span>
                    <span className="text-xs text-gray-500">({user.is_intern ? 'intern' : user.role})</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Message content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message *
            </label>
            <textarea
              required
              value={formData.content}
              onChange={(e) => setFormData({...formData, content: e.target.value})}
              rows="6"
              placeholder="Type your message..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Toggle Replies Checkbox - Only for team announcements */}
          {messageType === 'team_announcement' && (
            <div className="border-t border-gray-200 pt-4">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={repliesDisabled}
                  onChange={(e) => setRepliesDisabled(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-700">
                    Disable replies (announcement only)
                  </span>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Players won't be able to reply. Coaches and admins can still respond.
                  </p>
                </div>
              </label>
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Message'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NewChatRoomModal({ users, userId, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    title: '',
    recipientIds: [],
    content: ''
  });
  const [recipientSearch, setRecipientSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const toggleRecipient = (recipientId) => {
    setFormData(prev => ({
      ...prev,
      recipientIds: prev.recipientIds.includes(recipientId)
        ? prev.recipientIds.filter(id => id !== recipientId)
        : [...prev.recipientIds, recipientId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data: conversation, error: convError } = await supabase
        .from('conversations')
        .insert({
          type: 'group',
          title: formData.title,
          created_by: userId,
          is_pinned: false,
          replies_disabled: false
        })
        .select()
        .single();

      if (convError) throw convError;

      const participants = [
        { conversation_id: conversation.id, user_id: userId },
        ...formData.recipientIds.map(id => ({
          conversation_id: conversation.id,
          user_id: id
        }))
      ];

      const { error: participantsError } = await supabase
        .from('conversation_participants')
        .insert(participants);

      if (participantsError) throw participantsError;

      if (formData.content.trim()) {
        const { error: messageError } = await supabase
          .from('messages')
          .insert({
            conversation_id: conversation.id,
            sender_id: userId,
            content: formData.content.trim()
          });

        if (messageError) throw messageError;
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">New Chat</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Chat Room Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Chat Name *
            </label>
            <input
              type="text"
              required
              placeholder="e.g., Game Strategy, Social, Varsity Squad"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Member Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Invite Members *
            </label>
            <input
              type="text"
              value={recipientSearch}
              onChange={(e) => setRecipientSearch(e.target.value)}
              placeholder="Search by name or role..."
              className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
            <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
              {users
                .filter(u => u.id !== userId)
                .filter(u => {
                  const q = recipientSearch.trim().toLowerCase();
                  if (!q) return true;
                  return (u.full_name || '').toLowerCase().includes(q) || (u.role || '').toLowerCase().includes(q);
                })
                .map(user => (
                <label
                  key={user.id}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                >
                  <input
                    type="checkbox"
                    checked={formData.recipientIds.includes(user.id)}
                    onChange={() => toggleRecipient(user.id)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-900">{user.full_name}</span>
                  <span className="text-xs text-gray-500">({user.role})</span>
                </label>
              ))}
            </div>
            {formData.recipientIds.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">{formData.recipientIds.length} member(s) selected</p>
            )}
          </div>

          {/* Initial Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Initial Message (optional)
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({...formData, content: e.target.value})}
              rows="4"
              placeholder="Send an optional first message..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || formData.recipientIds.length === 0}
              className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 transition disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Chat'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
